import { useCallback } from 'react';

import { UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { triggerConfetti } from '@components/confetti/provider';
import { showInfo, showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { downloadFile } from '@utils/browser';

import { useInvalidateTravellers } from 'api/company-traveller/hooks';
import { useCompleteOnboardingStep } from 'api/company/hooks';
import { useWebSocket, waitForElasticRefresh } from 'api/hooks';
import { useInvalidateJourneyActionForms } from 'api/journey-action-forms/hooks';
import {
  handleError,
  handlePossibleFieldError,
  handlePossibleNonFieldError,
} from 'api/query-client';
import {
  getActivityLogsQueryKey,
  getJourneyTravellerActionQueryKey,
  getTravellerJourneyActionsQueryKey,
} from 'api/traveller-actions/hooks';
import { JourneyType, NonFieldError, TravellerJourney, TravellerJourneyListItem } from 'api/types';

import {
  bulkCompleteJourneyTravellers,
  bulkCompletePreview,
  bulkCreateJourneyTraveller,
  bulkDeleteJourneyTraveller,
  BulkEditFieldsResponse,
  bulkEditTravellerJourneyFields,
  bulkPauseJourneyTraveller,
  bulkResetJourneyTraveller,
  bulkResumeJourneyTraveller,
  bulkStartJourneyTraveller,
  bulkUndoCompleteJourneyTravellers,
  changeModifiedTravellerJourneys,
  createJourneyTraveller,
  deleteJourneyTraveller,
  editJourneyTraveller,
  fetchTravellerFields,
  getBulkCreateTemplate,
  getJourneyTraveller,
  getJourneyTravellerIssues,
  getTravellerJourneyFormSubmission,
  getTravellerJourneySource,
  loadJourneyActivityLogs,
  getTravellerJourneysStatus,
  loadJourneyTravellersV2,
  loadModifiedTravellerJourneys,
  pauseJourneyTraveller,
  resetJourneyTraveller,
  resumeJourneyTraveller,
  startJourneyTraveller,
} from './requests';
import { BulkCompleteResponse, BulkCreateTraveller, BulkUndoCompleteResponse } from './types';

const UNDO_TOAST_DURATION_SECONDS = 10;

export const useJourneyTravellersRaw = (journeyId: string) => {
  const query = useQuery({
    queryKey: ['travellers', journeyId],
    queryFn: () => loadJourneyTravellersV2(journeyId),
  });
  const queryClient = useQueryClient();

  const refetchTravellers = useCallback(() => {
    waitForElasticRefresh(async () => {
      await queryClient.refetchQueries(['travellers', journeyId]);
    });
  }, [queryClient, journeyId]);

  useWebSocket(`journeys/${journeyId}/`, refetchTravellers);

  return query;
};

export const useFetchTravellerFields = (journeyId: string) => {
  return useMutation({
    mutationFn: (fieldIds: string[]) => fetchTravellerFields(journeyId, fieldIds),
  });
};

const getJourneyTravellerQueryKey = (travellerJourneyId?: TravellerJourney['shortId']) =>
  ['traveller', travellerJourneyId].filter(Boolean);

export const useJourneyTraveller = (id?: string) => {
  const queryKey = getJourneyTravellerQueryKey(id);
  const query = useQuery({
    queryKey,
    queryFn: () => getJourneyTraveller(id!),
    enabled: Boolean(id),
  });
  const queryClient = useQueryClient();
  useWebSocket(`traveller-journey/${id}/`, () => {
    queryClient.invalidateQueries(queryKey);
    queryClient.invalidateQueries(getTravellerJourneyActionsQueryKey());
  });
  return query;
};

export const useJourneyTravellerIssues = (travellerId: string) => {
  return useQuery({
    queryKey: ['traveller', travellerId, 'issues'],
    queryFn: () => getJourneyTravellerIssues(travellerId),
  });
};

const useInvalidateJourneyTravellerFull = ({
  journeyId,
  travellerJourneyId,
}: {
  journeyId: JourneyType['shortId'];
  travellerJourneyId: TravellerJourney['shortId'];
}) => {
  const queryClient = useQueryClient();
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();

  return () => {
    waitForElasticRefresh(async () => {
      queryClient.invalidateQueries(['travellers', journeyId]);
      await queryClient.invalidateQueries(getJourneyTravellerQueryKey(travellerJourneyId));
      queryClient.invalidateQueries(getTravellerJourneyActionsQueryKey(travellerJourneyId));
      queryClient.invalidateQueries(getJourneyActivityLogsQueryKey(travellerJourneyId));
      invalidateJourneyActionFormsList();
    });
  };
};

export const usePauseJourneyTraveller = ({
  travellerJourneyId,
  journeyId,
  options,
}: {
  travellerJourneyId: string;
  journeyId: string;
  options?: UseMutationOptions<unknown, any, any>;
}) => {
  const { t } = useScopedTranslate(locale);

  const invalidateJourneyTravellerFull = useInvalidateJourneyTravellerFull({
    journeyId,
    travellerJourneyId,
  });
  return useMutation({
    ...options,
    mutationFn: () => pauseJourneyTraveller(travellerJourneyId),
    onSuccess: (...data) => {
      invalidateJourneyTravellerFull();
      const successText = t('journeyPaused');
      showSuccess(successText);
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('journeyNotPaused'));
    },
  });
};

export const useGetTravellerJourneysStatus = (ids: string[], companytravellerId: string) => {
  return useQuery({
    queryKey: ['traveller-journeys-status', companytravellerId],
    queryFn: () => getTravellerJourneysStatus(ids),
  });
};

export const useGetTravellerJourneySource = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-journey-source', shortId],
    queryFn: () => getTravellerJourneySource(shortId),
  });
};

export const useTravellerJourneyFormSubmission = (shortId: string) =>
  useQuery({
    queryKey: ['traveller-journey-form-submission', shortId],
    queryFn: () => getTravellerJourneyFormSubmission(shortId),
  });

export const useResumeJourneyTraveller = ({
  travellerJourneyId,
  journeyId,
  options,
}: {
  travellerJourneyId: string;
  journeyId: string;
  options?: UseMutationOptions<unknown, any, any>;
}) => {
  const { t } = useScopedTranslate(locale);

  const invalidateJourneyTravellerFull = useInvalidateJourneyTravellerFull({
    journeyId,
    travellerJourneyId,
  });
  return useMutation({
    ...options,
    mutationFn: () => resumeJourneyTraveller(travellerJourneyId),
    onSuccess: (...data) => {
      invalidateJourneyTravellerFull();
      const successText = t('journeyResumed');
      showSuccess(successText);
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('journeyNotResumed'));
    },
  });
};

export const useResetJourneyTraveller = ({
  travellerJourneyId,
  journeyId,
  options,
}: {
  travellerJourneyId: string;
  journeyId: string;
  options?: UseMutationOptions<unknown, any, any>;
}) => {
  const { t } = useScopedTranslate(locale);

  const invalidateJourneyTravellerFull = useInvalidateJourneyTravellerFull({
    journeyId,
    travellerJourneyId,
  });
  return useMutation({
    ...options,
    mutationFn: () => resetJourneyTraveller(travellerJourneyId),
    onSuccess: (...data) => {
      invalidateJourneyTravellerFull();
      const successText = t('journeyReset');
      showSuccess(successText);
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('journeyNotReset'));
    },
  });
};

export const useStartJourneyTraveller = ({
  travellerJourneyId,
  journeyId,
  options,
}: {
  travellerJourneyId: string;
  journeyId: string;
  options?: UseMutationOptions<unknown, NonFieldError, any>;
}) => {
  const { t } = useScopedTranslate(locale);

  const invalidateJourneyTravellerFull = useInvalidateJourneyTravellerFull({
    journeyId,
    travellerJourneyId,
  });
  const completeOnboardingStep = useCompleteOnboardingStep();
  return useMutation({
    ...options,
    mutationFn: () => startJourneyTraveller(travellerJourneyId),
    onSuccess: (...data) => {
      invalidateJourneyTravellerFull();
      const successText = t('journeyInitated');
      showSuccess(successText);
      options?.onSuccess?.(...data);
      triggerConfetti();
      completeOnboardingStep('runDemoJourney', travellerJourneyId);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('journeyNotInitated'), invalidateJourneyTravellerFull);
    },
  });
};

export const useCreateJourneyTraveller = (
  id: string,
  options?: UseMutationOptions<{ id: string }, NonFieldError, FormData>,
) => {
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: createJourneyTraveller,
    onSuccess: (data, variables, context) => {
      // We don't invalidate traveller queries here because WebSocket notifications
      // will trigger refetches after elastic search indexing completes
      queryClient.invalidateQueries(['travellers', 'actions', id]);
      options?.onSuccess?.(data, variables, context);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('travellerNotAddedToJourney'));
    },
  });
};

export const useEditJourneyTraveller = (
  id: string,
  journey: string,
  options?: UseMutationOptions<unknown, NonFieldError, FormData>,
) => {
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => editJourneyTraveller(id, data),
    onSuccess: (...data) => {
      queryClient.invalidateQueries(getJourneyTravellerQueryKey(id));
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers', journey]);
        queryClient.invalidateQueries(['traveller', id]);
      });
      showSuccess(t('travellerEdited'));
      options?.onSuccess?.(...data);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('travellerNotEdited'));
    },
  });
};

type DeleteTravellerJourneyProps = {
  id: string;
  journey: string;
  onSuccess?: () => void;
};
export function useDeleteTravellerJourney(props: DeleteTravellerJourneyProps) {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();

  return useMutation(() => deleteJourneyTraveller(props.id), {
    onSuccess: () => {
      showSuccess(t('travellerDeleted'));
      queryClient.setQueryData<TravellerJourneyListItem[]>(
        ['travellers', props.journey],
        (old) => old?.filter((traveller) => traveller.shortId !== props.id) ?? [],
      );
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers', props.journey]);
        queryClient.invalidateQueries(['traveller-journeys-status']);
        queryClient.invalidateQueries(['company-travellers']);
        queryClient.invalidateQueries(['company-traveller']);
      });
      props.onSuccess?.();
    },
    onError: () => {
      handleError(t('travellerNotDeleted'));
    },
  });
}

export const useBulkStartTravellerJourney = (
  journeyId: string,
  options?: UseMutationOptions<unknown, unknown, { ids: string[] }>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();
  return useMutation({
    ...options,
    mutationFn: (data) => bulkStartJourneyTraveller({ journeyId, data }),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      showSuccess(t('journeysStarted'));
      queryClient.invalidateQueries(['travellers', journeyId]);
      invalidateJourneyActionFormsList();
      triggerConfetti();
    },
    onError: () => {
      handleError(t('journeysNotStarted'), () =>
        queryClient.invalidateQueries(['travellers', journeyId]),
      );
    },
  });
};

export const useBulkDeleteTravellerJourney = (
  journeyId: string,
  options?: UseMutationOptions<unknown, unknown, { ids: string[] }>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => bulkDeleteJourneyTraveller({ journeyId, data }),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      showSuccess(t('travellersDeleted'));
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers', journeyId]);
      });
    },
    onError: () => {
      handleError(t('travellersNotDeleted'), () =>
        queryClient.invalidateQueries(['travellers', journeyId]),
      );
    },
  });
};

export const useBulkPauseTravellerJourney = (
  journeyId: string,
  options?: UseMutationOptions<unknown, unknown, { ids: string[] }>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => bulkPauseJourneyTraveller({ journeyId, data }),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      showSuccess(t('journeysPaused'));
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers', journeyId]);
      });
    },
    onError: () => {
      handleError(t('journeysNotPaused'), () =>
        queryClient.invalidateQueries(['travellers', journeyId]),
      );
    },
  });
};

export const useBulkResumeTravellerJourney = (
  journeyId: string,
  options?: UseMutationOptions<unknown, unknown, { ids: string[] }>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => bulkResumeJourneyTraveller({ journeyId, data }),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      showSuccess(t('journeysResumed'));
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers', journeyId]);
      });
    },
    onError: () => {
      handleError(t('journeysNotResumed'), () =>
        queryClient.invalidateQueries(['travellers', journeyId]),
      );
    },
  });
};

export const useBulkResetTravellerJourney = (
  journeyId: string,
  options?: UseMutationOptions<unknown, unknown, { ids: string[] }>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();
  return useMutation({
    ...options,
    mutationFn: (data) => bulkResetJourneyTraveller({ journeyId, data }),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      showSuccess(t('journeysReset'));
      queryClient.invalidateQueries(['travellers', journeyId]);
      invalidateJourneyActionFormsList();
    },
    onError: () => {
      handleError(t('journeysNotReset'), () =>
        queryClient.invalidateQueries(['travellers', journeyId]),
      );
    },
  });
};

export const getJourneyActivityLogsQueryKey = (journeyId?: string) =>
  ['journey-activity-logs', journeyId].filter(Boolean);

export const getBulkCompletePreviewQueryKey = (journeyId: string, ids: string[]) => [
  'traveller-journeys-complete-preview',
  journeyId,
  ids,
];

export const useBulkCompletePreview = (journeyId: string, ids: string[]) => {
  return useQuery({
    queryKey: getBulkCompletePreviewQueryKey(journeyId, ids),
    queryFn: () => bulkCompletePreview({ journeyId, ids }),
    enabled: ids.length > 0,
  });
};

const useInvalidateAfterManualCompletion = (journeyId: string) => {
  const queryClient = useQueryClient();
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();
  return (travellerJourneyIds: string[]) => {
    waitForElasticRefresh(() => {
      queryClient.invalidateQueries(['travellers', journeyId]);
    });
    travellerJourneyIds.forEach((travellerJourneyId) => {
      queryClient.invalidateQueries(getJourneyTravellerQueryKey(travellerJourneyId));
      queryClient.invalidateQueries(getTravellerJourneyActionsQueryKey(travellerJourneyId));
      queryClient.invalidateQueries(getJourneyActivityLogsQueryKey(travellerJourneyId));
    });
    queryClient.invalidateQueries(getActivityLogsQueryKey());
    queryClient.invalidateQueries(getJourneyTravellerActionQueryKey());
    invalidateJourneyActionFormsList();
  };
};

const useBulkUndoCompleteTravellerJourneys = (journeyId: string) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const invalidate = useInvalidateAfterManualCompletion(journeyId);
  return useMutation({
    mutationFn: (data: { ids: string[] }) => bulkUndoCompleteJourneyTravellers({ journeyId, data }),
    onSuccess: (response: BulkUndoCompleteResponse) => {
      const count = response.restoredIds.length;
      invalidate(response.restoredIds);
      if (count === 0) {
        showInfo(t('noCompletionUndone'));
        return;
      }
      showSuccess(count === 1 ? t('completionUndone') : t('completionsUndone', { count }));
    },
    onError: () => {
      handleError(t('completionNotUndone'), () =>
        queryClient.invalidateQueries(['travellers', journeyId]),
      );
    },
  });
};

export const useBulkCompleteTravellerJourneys = (
  journeyId: string,
  options?: UseMutationOptions<BulkCompleteResponse, unknown, { ids: string[]; reason: string }>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const invalidate = useInvalidateAfterManualCompletion(journeyId);
  const undoMutation = useBulkUndoCompleteTravellerJourneys(journeyId);
  return useMutation({
    ...options,
    mutationFn: (data) => bulkCompleteJourneyTravellers({ journeyId, data }),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      const [response, variables] = data;
      const count = response.completedIds.length;
      if (count === 0) {
        invalidate(response.completedIds);
        showInfo(
          variables.ids.length === 1 ? t('travellerAlreadyCompleted') : t('noTravellersCompleted'),
        );
        return;
      }
      showSuccess(count === 1 ? t('travellerCompleted') : t('travellersCompleted', { count }), {
        customButtons: [
          {
            text: t('undo'),
            onClick: () => undoMutation.mutate({ ids: response.completedIds }),
          },
        ],
        duration: UNDO_TOAST_DURATION_SECONDS,
      });
      invalidate(response.completedIds);
    },
    onError: () => {
      handleError(t('travellersNotCompleted'), () =>
        queryClient.invalidateQueries(['travellers', journeyId]),
      );
    },
  });
};

export const useBulkEditTravellerJourneyFields = (
  journeyId: string,
  options?: UseMutationOptions<BulkEditFieldsResponse, unknown, FormData>,
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyTravellers } = useInvalidateTravellers();
  return useMutation({
    ...options,
    mutationFn: (data) => bulkEditTravellerJourneyFields({ journeyId, data }),
    onSuccess: (...args) => {
      const { updated } = args[0];
      if (updated === 0) {
        showInfo(t('noTravellersUpdated'));
        return;
      }
      options?.onSuccess?.(...args);
      showSuccess(updated === 1 ? t('fieldsUpdatingOne') : t('fieldsUpdating', { count: updated }));
      waitForElasticRefresh(() => invalidateJourneyTravellers(journeyId));
    },
    onError: (error) => {
      handlePossibleFieldError(error, t('fieldsNotUpdated'));
    },
  });
};

type BulkErrorResponse = AxiosError<{
  travellers?: { [key: string]: string[] }[];
}>;

export const useBulkCreateTravellerJourney = (
  journeyId: string,
  setErrors: (errors: { travellers?: { [key: string]: string[] }[] }) => void,
  options?: UseMutationOptions<
    unknown,
    unknown,
    { travellers: BulkCreateTraveller[]; isSave: boolean; updateExisting?: boolean }
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: (data) => bulkCreateJourneyTraveller(journeyId, data),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
    },
    onError: (errors: BulkErrorResponse) => {
      const errorData = errors.response?.data ?? {};
      if (errorData?.travellers) {
        setErrors(errorData);
      }
    },
  });
};

export const useBulkCreateJourneyTravellerTemplate = (journeyId: string) => {
  return useMutation({
    mutationFn: (data) => getBulkCreateTemplate(journeyId),
    onSuccess: (response) => {
      downloadFile(response, 'journey-traveller-import-template.csv');
    },
  });
};
const getModifiedTravellerJourneysQueryKey = (journeyId: string) => [
  'modified-traveller-journeys',
  journeyId,
];

export const useGetModifiedTravellerJourneys = (journeyId: string) => {
  return useQuery({
    queryKey: getModifiedTravellerJourneysQueryKey(journeyId),
    queryFn: () => loadModifiedTravellerJourneys(journeyId),
  });
};

export const useChangeModifiedTravellerJourneys = (journeyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: string[]) => changeModifiedTravellerJourneys(data),
    onSuccess: () => {
      queryClient.invalidateQueries(getModifiedTravellerJourneysQueryKey(journeyId));
    },
  });
};

const locale = {
  en: {
    travellerAddedToJourney: 'Traveller added to journey',
    travellerNotAddedToJourney:
      'Could not create traveller, please review traveller details and try again',
    travellerEdited: 'Traveller updated successfully',
    travellerNotEdited: 'Traveller has not been edited',
    travellerDeleted: 'Traveller removed from journey',
    travellerNotDeleted: 'Traveller not deleted',
    travellersDeleted: 'Travellers removed from journey',
    travellersNotDeleted: 'Travellers could not be deleted',
    journeyInitated: 'Journey initiated!',
    journeyNotInitated: 'Journey could not be initiated!',
    journeyPaused: 'Journey paused',
    journeyNotPaused: 'Journey could not be paused',
    journeyResumed: 'Journey resumed',
    journeyNotResumed: 'Journey could not be resumed',
    journeysNotStarted: 'Journeys could not be started',
    journeysStarted: 'Journeys started successfully',
    journeyReset: 'Journey reset',
    journeyNotReset: 'Journey could not be reset',
    journeysPaused: 'Journeys paused successfully',
    journeysNotPaused: 'Journeys could not be paused',
    journeysResumed: 'Journeys resumed successfully',
    journeysNotResumed: 'Journeys could not be resumed',
    journeysReset: 'Journeys reset successfully',
    journeysNotReset: 'Journeys could not be reset',
    fieldsUpdating: 'Updating fields for {count} travellers',
    fieldsUpdatingOne: 'Updating fields for 1 traveller',
    noTravellersUpdated: 'No travellers were updated',
    fieldsNotUpdated: 'Could not update fields',
    travellerCompleted: 'Traveller marked as completed',
    travellersCompleted: '{count} travellers marked as completed',
    travellersNotCompleted: 'Travellers could not be marked as completed',
    completionUndone: 'Completion undone for 1 traveller',
    completionsUndone: 'Completion undone for {count} travellers',
    noCompletionUndone: 'Nothing to undo',
    travellerAlreadyCompleted: 'This traveller was already completed',
    noTravellersCompleted: 'These travellers were already completed',
    completionNotUndone: 'Could not undo the completion',
    undo: 'Undo',
  },
};

export const useJourneyActivityLogs = (journeyId?: string) => {
  return useQuery({
    queryKey: getJourneyActivityLogsQueryKey(journeyId),
    queryFn: () => loadJourneyActivityLogs(journeyId!),
    enabled: Boolean(journeyId),
  });
};
